import {
  ChangeEvent,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import useDebounce from "../../hooks/useDebounce";

import Loading from "../../components/Loading";
import NoContent from "../../components/UIelements/NoContent";
import PrimaryButton from "../../components/FormElements/PrimaryButton";
import ModalAddSerialNumber from "../../components/Modals/ModalAddSerialNumber";
import SearchBar from "../../components/UIelements/SearchBar";
import ModalDeleteSerialNumber from "../../components/Modals/ModalDeleteSerialNumber";
import ModalEditSerialNumber from "../../components/Modals/ModalEditSerialNumber";
import TableSerialNumbers from "./tableSerialNumber";

import { SerialNumbersModel } from "../../models/serial-numbers";
import { loadSerialNumbers } from "../../services/serialNumber";

import * as Styled from "./styles";

import { useSelector } from "react-redux";
import { RootState } from "../../store";

import { createScrollHandler } from "../../utils/createScrollHandler";
import { useHeader } from "../../context/HeaderContext";

const SerialNumbers = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { idCompany } = useParams<{ idCompany: string }>();
  const globalUser = useSelector((state: RootState) => state.user.object);
  const globalCompanies = useSelector((state: RootState) => state.companies);

  const [serialNumbers, setSerialNumbers] = useState<SerialNumbersModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchValue, 1000);

  const [selectSerialNumber, setSelectSerialNumber] = useState<number>();
  const [selectedMarkerList, setSelectedMarkerList] = useState<Array<number>>(
    [],
  );
  const [showModalAddSerialNumber, setShowModalAddSerialNumber] =
    useState(false);
  const [showModalDeleteSerialNumber, setShowModalDeleteSerialNumber] =
    useState(false);
  const [showModalUpdateSerialNumber, setShowModalUpdateSerialNumber] =
    useState(false);

  const [totalCount, setTotalCount] = useState<number>(0);
  const [cursor, setCursor] = useState<{ id: number; serial: string } | null>(
    null,
  );
  const [hasMore, setHasMore] = useState<boolean>(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const divContentRef = useRef<HTMLDivElement>(null);

  const fetchInitialSerialNumbers = useCallback(
    async (search: string) => {
      if (!idCompany) return;
      setIsLoading(true);
      setCursor(null);
      setSerialNumbers([]);

      try {
        const resp = await loadSerialNumbers(
          globalUser?.user.accessToken ?? "",
          {
            companyId: idCompany,
            searchTerm: search,
          },
        );

        const newSerialNumbers = resp.data.SerialNumbers;
        setSerialNumbers(newSerialNumbers);
        setTotalCount(resp.data.totalCount);

        if (newSerialNumbers.length > 0) {
          const lastSerial = newSerialNumbers[newSerialNumbers.length - 1];
          setCursor({
            id: lastSerial.id ?? 0,
            serial: lastSerial.serialNumber,
          });
        }

        setHasMore(newSerialNumbers.length < resp.data.totalCount);
      } catch (error) {
        console.error(error);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    [globalUser, idCompany],
  );

  const loadMoreSerialNumbers = useCallback(async () => {
    if (isLoading || !hasMore || !cursor || !idCompany) return;

    setIsLoading(true);
    try {
      const resp = await loadSerialNumbers(globalUser?.user.accessToken ?? "", {
        companyId: idCompany,
        searchTerm: debouncedSearchTerm,
        lastId: cursor.id,
        lastSerialNumber: cursor.serial,
      });

      const newSerialNumbers = resp.data.SerialNumbers;
      setSerialNumbers((prev) => [...prev, ...newSerialNumbers]);
      setTotalCount(resp.data.totalCount);

      if (newSerialNumbers.length > 0) {
        const lastSerial = newSerialNumbers[newSerialNumbers.length - 1];
        setCursor({ id: lastSerial.id ?? 0, serial: lastSerial.serialNumber });
      }

      setHasMore(
        serialNumbers.length + newSerialNumbers.length < resp.data.totalCount,
      );
    } catch (error) {
      console.error(error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    hasMore,
    cursor,
    globalUser,
    debouncedSearchTerm,
    idCompany,
    serialNumbers.length,
  ]);

  useEffect(() => {
    fetchInitialSerialNumbers(debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchInitialSerialNumbers]);

  /* 
    const openMap = useCallback(() => {
      setShowMap(true);
    }, []);
  
    const closeMap = useCallback(() => {
      setShowMap(false);
    }, []); */

  const handleSearchValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [],
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      createScrollHandler(
        isLoading,
        hasMore,
        loadMoreSerialNumbers,
        scrollTimeoutRef,
      )(e);
    },
    [isLoading, hasMore, loadMoreSerialNumbers, scrollTimeoutRef],
  );

  const handleRefreshDeleteSerialNumbers = () => {
    setSelectedMarkerList([]);
    setShowModalDeleteSerialNumber(false);
    fetchInitialSerialNumbers(searchValue);
  };

  const headerConfig = useMemo(() => {
    return {
      showBack: true,
      backLabel: intl.formatMessage({ id: "menus.VOLTAR" }),
      backAction: () => navigate("/companies"),
    };
  }, [navigate, intl]);

  useHeader(headerConfig);

  return (
    <>
      {showModalAddSerialNumber && (
        <div className="modal">
          <ModalAddSerialNumber
            idCompany={idCompany!}
            onCancel={() => setShowModalAddSerialNumber(false)}
            refresh={() => fetchInitialSerialNumbers(searchValue)}
          />
        </div>
      )}

      {showModalDeleteSerialNumber && (
        <div className="modal">
          <ModalDeleteSerialNumber
            onCancel={() => setShowModalDeleteSerialNumber(false)}
            refresh={handleRefreshDeleteSerialNumbers}
            selectSerialNumber={
              selectedMarkerList.length
                ? selectedMarkerList
                : [selectSerialNumber ?? 0]
            }
          />
        </div>
      )}

      {showModalUpdateSerialNumber && (
        <div className="modal">
          <ModalEditSerialNumber
            onCancel={() => setShowModalUpdateSerialNumber(false)}
            refresh={() => fetchInitialSerialNumbers(searchValue)}
            selectSerialNumber={selectSerialNumber ?? 0}
            serialNumbers={serialNumbers.find(
              (e: any) => e.id === selectSerialNumber,
            )}
          />
        </div>
      )}

      {/*showMap && (
        <Map
          markerList={equipmentAddress()}
          onClose={closeMap}
          language={'PT'}
          localization={localizationEquipment}
        />
      )*/}
      <Styled.Header>
        <Styled.CompanyName>
          {globalCompanies.companies?.corporateName ?? ""}
        </Styled.CompanyName>

        <Styled.Location>
          <Styled.Country>
            {intl.formatMessage({ id: "equipment.país" }) + ": "}
            <div>{globalCompanies.companies?.location}</div>
          </Styled.Country>
          {/* <Styled.Map onClick={openMap}>  <MdLocationOn />{intl.formatMessage({ id: 'menus.ver localização' })}</Styled.Map> */}
        </Styled.Location>

        <Styled.EquipmentManager>
          <Styled.InputDiv>
            <SearchBar
              value={searchValue}
              onChange={handleSearchValue}
              placeholder={intl.formatMessage({
                id: `${window.innerWidth > 960 ? "equipment.Procurar número de série" : "main.Procurar"}`,
              })}
            />
          </Styled.InputDiv>

          <Styled.ButtonsDiv>
            <PrimaryButton
              model="confirmation-gradient"
              type="button"
              size="extra-large"
              width="168px"
              onClick={() => {
                setShowModalAddSerialNumber(true);
              }}
            >
              {intl
                .formatMessage({ id: "equipment.adicionar equipamento" })
                .toUpperCase()}
            </PrimaryButton>
          </Styled.ButtonsDiv>
          <Styled.ButtonsDiv id="user-tab__mobile-button">
            <PrimaryButton
              model="confirmation-gradient"
              type="button"
              size="medium"
              width="112px"
              onClick={() => setShowModalAddSerialNumber(true)}
            >
              {intl.formatMessage({ id: "company.Adicionar" }).toUpperCase()}
            </PrimaryButton>
          </Styled.ButtonsDiv>
        </Styled.EquipmentManager>
      </Styled.Header>

      <Styled.Content ref={divContentRef}>
        {isLoading && serialNumbers.length === 0 && <Loading />}
        {!isLoading && serialNumbers.length === 0 && (
          <NoContent
            isVertical
            title={intl.formatMessage({
              id: "company.AINDA NÃO HÁ EQUIPAMENTOS CADASTRADOS :(",
            })}
            description={intl.formatMessage({
              id: "company.description.AINDA NÃO HÁ EQUIPAMENTOS CADASTRADOS :(",
            })}
          />
        )}
        {serialNumbers.length > 0 && (
          <Styled.TableContent>
            <TableSerialNumbers
              serialNumbers={serialNumbers}
              selectSerialNumber={setSelectSerialNumber}
              showModalDeleteSerialNumber={() =>
                setShowModalDeleteSerialNumber(true)
              }
              showModalUpdateSerialNUmber={() =>
                setShowModalUpdateSerialNumber(true)
              }
              selectedMarker={setSelectedMarkerList}
              serialNumberCheck={selectedMarkerList}
              onScroll={handleScroll}
            />
          </Styled.TableContent>
        )}
      </Styled.Content>
    </>
  );
};

export default SerialNumbers;
